import { STUDIO_COPY } from '../config';

/**
 * Flip when `submit_accept` carries the full persisted mix-state (Phase 0).
 * Until then the V2 shell keeps the existing Approve & Download wording.
 */
export const RECAP_EDITOR_V2_PHASE0_MIX_CARRY = true;

export const RECAP_EDITOR_V2_TOOLS = [
  { id: 'clips', label: STUDIO_COPY.recapEditorV2ToolClips },
  { id: 'mix', label: STUDIO_COPY.recapEditorV2ToolMix },
  { id: 'music', label: STUDIO_COPY.recapEditorV2ToolMusic },
  { id: 'voice', label: STUDIO_COPY.recapEditorV2ToolVoice },
  { id: 'branding', label: STUDIO_COPY.recapEditorV2ToolBranding },
] as const;

export type RecapEditorV2ToolId = (typeof RECAP_EDITOR_V2_TOOLS)[number]['id'];

export type RecapEditorChipId = 'draft' | 'finalizing' | 'finalized';

export type RecapEditorStateOpts = {
  accepted?: boolean | null;
  acceptedAt?: string | null;
  mixStatus?: string | null;
  dirty?: boolean;
  acceptBusy?: boolean;
  jobStatus?: string | null;
};

export function recapEditorChip(opts: RecapEditorStateOpts): RecapEditorChipId {
  if (opts.acceptBusy) return 'finalizing';
  const accepted = opts.accepted === true || Boolean(opts.acceptedAt);
  if (!accepted || opts.dirty) return 'draft';
  if (String(opts.mixStatus || '') === 'applying') return 'finalizing';
  const status = String(opts.jobStatus || '').trim().toLowerCase();
  if (status === 'rendering' || status === 'accepted') return 'finalizing';
  return 'finalized';
}

export function recapEditorChipLabel(chip: RecapEditorChipId): string {
  if (chip === 'finalizing') return STUDIO_COPY.recapEditorV2Finalizing;
  if (chip === 'finalized') return STUDIO_COPY.recapEditorV2Finalized;
  return STUDIO_COPY.recapEditorV2Draft;
}

export function isRecapFinalized(opts: RecapEditorStateOpts): boolean {
  return recapEditorChip(opts) === 'finalized';
}

export function recapEditorFinalizeLabel(opts: {
  acceptBusy?: boolean;
  phase0Carry?: boolean;
}): string {
  if (opts.acceptBusy) return STUDIO_COPY.acceptCtaBusy;
  if (opts.phase0Carry ?? RECAP_EDITOR_V2_PHASE0_MIX_CARRY) {
    return STUDIO_COPY.recapEditorV2Finalize;
  }
  return STUDIO_COPY.acceptCtaWithCost;
}

export function recapEditorCarryFootnote(
  phase0Carry = RECAP_EDITOR_V2_PHASE0_MIX_CARRY
): string | null {
  return phase0Carry ? null : STUDIO_COPY.recapEditorV2CarryFootnote;
}

export type RecapIncludesItem = {
  id: 'clips' | 'music' | 'voice' | 'watermark';
  label: string;
  present: boolean;
};

export function buildRecapIncludesItems(opts: {
  clipCount?: number | null;
  runtimeLabel?: string | null;
  musicLabel?: string | null;
  hasVoice?: boolean | null;
  watermarkOn?: boolean | null;
}): RecapIncludesItem[] {
  const clipCount = Number.isFinite(opts.clipCount)
    ? Math.max(0, Math.floor(Number(opts.clipCount)))
    : 0;
  const runtimeLabel = String(opts.runtimeLabel || '0:00').trim() || '0:00';
  const musicLabel =
    typeof opts.musicLabel === 'string' ? opts.musicLabel.trim() : '';
  const hasVoice = Boolean(opts.hasVoice);
  const watermarkOn = Boolean(opts.watermarkOn);
  const clips =
    STUDIO_COPY.clipCountTemplate.replace('{n}', String(clipCount)) +
    ` · ${runtimeLabel}`;
  return [
    { id: 'clips', label: clips, present: clipCount > 0 },
    {
      id: 'music',
      label: musicLabel || STUDIO_COPY.recapEditorV2NoMusic,
      present: Boolean(musicLabel),
    },
    {
      id: 'voice',
      label: hasVoice
        ? STUDIO_COPY.recapEditorV2VoiceOn
        : STUDIO_COPY.recapEditorV2NoVoice,
      present: hasVoice,
    },
    {
      id: 'watermark',
      label: watermarkOn
        ? STUDIO_COPY.recapEditorV2WatermarkOn
        : STUDIO_COPY.recapEditorV2NoWatermark,
      present: watermarkOn,
    },
  ];
}

export function nextRecapEditorTool(
  current: RecapEditorV2ToolId,
  delta: number
): RecapEditorV2ToolId {
  const ids = RECAP_EDITOR_V2_TOOLS.map((tool) => tool.id);
  const index = ids.indexOf(current);
  const next = (index + delta + ids.length) % ids.length;
  return ids[next];
}
